import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "context/TranslationProvider";
import { useNotification } from "providers/NotificationProvider";
import userServices from "services/userServices";
import SimpleHeader from "components/Headers/SimpleHeader";
import EvidenceModal from "./components/EvidenceModal";
import Pagination from "views/pages/components/Pagination";
import constants from "constants";
import helpers from "utils/helpers";
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  Button,
  Badge,
  Spinner,
  Alert,
  FormGroup,
  Input
} from "reactstrap";

// ─── Shared Helpers ──────────────────────────────────────────────────────────

const formatContext = (context, t) => {
  if (!context) return "";
  if (context.includes(".") && !context.includes(",")) {
    let translationKey = `page.userListings.rejectionReasons.${context}.title`;
    let translated = t(translationKey);
    if (translated && translated !== translationKey) return translated;
    translationKey = `page.users.details.rejectionReasons.${context}.title`;
    translated = t(translationKey);
    if (translated && translated !== translationKey) return translated;
  }
  if (context.includes(",")) {
    return context.split(",").map(token => {
      const cleanToken = token.trim();
      if (cleanToken.includes(".")) {
        let transKey = `page.userListings.rejectionReasons.${cleanToken}.title`;
        let translated = t(transKey);
        if (translated && translated !== transKey) return translated;
        transKey = `page.users.details.rejectionReasons.${cleanToken}.title`;
        translated = t(transKey);
        if (translated && translated !== transKey) return translated;
      }
      const transKey = `categories.moderation.context.${cleanToken}`;
      const translatedToken = t(transKey);
      if (translatedToken && translatedToken !== transKey) return translatedToken;
      return cleanToken.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
    }).join(", ");
  }
  const transKey = `categories.moderation.context.${context}`;
  const translated = t(transKey);
  if (translated && translated !== transKey) return translated;
  return context.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
};

const getStatusLabel = (status, t) => {
  if (!status) return "";
  const labels = {
    pending: t("page.flagged.status.pending", "Pending"),
    under_review: t("page.flagged.status.under_review", "Under Review"),
    underReview: t("page.flagged.status.under_review", "Under Review"),
    closed: t("page.flagged.status.closed", "Closed"),
    resolved: t("page.flagged.status.resolved", "Resolved"),
    dismissed: t("page.flagged.status.dismissed", "Dismissed"),
    decision_made: t("page.flagged.status.decision_made", "Decision Made"),
    action_taken: t("page.flagged.status.action_taken", "Action Taken"),
    actionTaken: t("page.flagged.status.action_taken", "Action Taken"),
    dismiss: t("page.flagged.status.dismiss", "Dismiss"),
    warn: t("page.flagged.status.warn", "Warn (Issue Warning)"),
    restrict: t("page.flagged.status.restrict", "Restrict Chat (24h)"),
    suspend: t("page.flagged.status.suspend", "Suspend User"),
    in_progress: t("page.flagged.status.in_progress", "In Progress"),
  };
  return labels[status] || status.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
};

const getStatusDisplay = (flag, t) => {
  if (flag.status === "decision_made") return getStatusLabel(flag.actionTaken, t);
  return getStatusLabel(flag.status, t);
};

const getStatusBadgeColor = (status) => {
  const map = {
    pending: "#fb6340",
    under_review: "#11cdef",
    underReview: "#11cdef",
    in_progress: "#11cdef",
    closed: "#2dce89",
    resolved: "#2dce89",
    dismissed: "#8898aa",
    decision_made: "#f5365c",
    action_taken: "#f5365c",
    actionTaken: "#f5365c",
    warn: "#fb6340",
    restrict: "#11cdef",
    suspend: "#f5365c",
    dismiss: "#2dce89",
  };
  return map[status] || "#8898aa";
};

const getSeverityBadgeColor = (severity) => {
  const map = {
    critical: { bg: "#f5365c", text: "#fff" },
    high: { bg: "#fb6340", text: "#fff" },
    medium: { bg: "#11cdef", text: "#fff" },
    low: { bg: "#e9ecef", text: "#525f7f" },
  };
  return map[severity] || map.low;
};

const getSeverityLabel = (severity) => {
  if (!severity) return "LOW";
  return severity.toUpperCase();
};

const getActiveDropdownItem = (flag) => {
  if (flag.status === "decision_made") return flag.actionTaken;
  if (flag.status === "closed" || flag.status === "resolved" || flag.status === "dismissed") return "dismiss";
  return flag.status;
};

// ─── User Mini Card (shared) ──────────────────────────────────────────────────

function UserMiniCard({ userObj, roleLabel, navigate }) {
  if (!userObj) return null;
  const name = `${userObj.firstName || ""} ${userObj.lastName || ""}`.trim() || "Unknown";
  const initials = name.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase() || "US";
  return (
    <div
      className="d-flex align-items-center p-3 rounded mb-3"
      style={{ backgroundColor: "#F8FAFC", border: "1px solid #E2E8F0" }}
    >
      <div
        className="d-flex align-items-center justify-content-center rounded-circle text-primary font-weight-bold mr-3"
        style={{ width: "44px", height: "44px", backgroundColor: "#EFF6FF", fontSize: "15px", border: "1px solid #DBEAFE", flexShrink: 0 }}
      >
        {initials}
      </div>
      <div className="flex-grow-1" style={{ minWidth: 0 }}>
        <div className="font-weight-bold text-slate-800 text-sm" style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{name}</div>
        <div className="text-muted" style={{ fontSize: "11px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{userObj.email || ""}</div>
        {roleLabel && (
          <Badge className="badge-pill mt-1 text-uppercase" style={{ fontSize: "9px", backgroundColor: "#EFF6FF", color: "#2563EB" }}>
            {roleLabel}
          </Badge>
        )}
      </div>
    </div>
  );
}

// ─── DISPUTE DETAIL VIEW ──────────────────────────────────────────────────────

function DisputeDetailView({ flag, t, navigate, getEvidenceUrl }) {
  const [evidenceOpen, setEvidenceOpen] = useState(false);
  const [selectedEvidenceUrl, setSelectedEvidenceUrl] = useState("");

  // The primary dispute is the first linkedDisputeId (most recent)
  const dispute = flag.linkedDisputeIds?.[0] || null;
  const order = dispute?.orderid || null;
  const userObj = flag.userId || {};
  const userName = `${userObj.firstName || ""} ${userObj.lastName || ""}`.trim() || "Unknown User";
  const userInitials = userName.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase() || "US";

  const flagPriority = flag.priority || flag.severity || dispute?.priority;

  const disputeCategoryLabel = {
    missed_delivery: "Missed Delivery",
    damaged_items: "Damaged Items",
    illegal_items: "Illegal Items",
    fake_person: "Fake Person",
  };

  const orderStatusColor = {
    Confirmed: "#2dce89",
    "Picked Up": "#11cdef",
    Delivered: "#5e72e4",
    Cancelled: "#f5365c",
    Failed: "#f5365c",
    frozen_compliance: "#8898aa",
  };

  return (
    <>
      <SimpleHeader
        name={t("categories.moderation.disputeDetails", "Dispute Investigation")}
        parentName={t("sideNav.behavioralQueue", "Behavioral & Disputes")}
      />

      <Container className="mt--6" fluid>
        {/* ── Profile Header Card ── */}
        <Row className="mb--2">
          <Col xs="12">
            <Card className="shadow-sm border-0 rounded-lg">
              <CardBody className="py-4 px-4 d-flex flex-column flex-md-row justify-content-between align-items-md-center">
                <div className="d-flex align-items-center mb-3 mb-md-0">
                  <div
                    className="d-flex align-items-center justify-content-center rounded-circle text-primary font-weight-bold mr-3"
                    style={{ width: "60px", height: "60px", backgroundColor: "#EFF6FF", fontSize: "20px", border: "1px solid #DBEAFE", flexShrink: 0 }}
                  >
                    {userInitials}
                  </div>
                  <div>
                    <h2 className="mb-0 text-slate-800 font-weight-bold" style={{ fontSize: "20px" }}>{userName}</h2>
                    <span className="text-muted text-sm">{userObj.email || ""}</span>
                  </div>
                  {userObj.phoneNumber && (
                    <div className="border-left pl-4 ml-4 d-none d-lg-block">
                      <span className="text-muted text-xs d-block text-uppercase font-weight-600 tracking-wider">{t("table.headers.phone", "Phone")}</span>
                      <span className="font-weight-bold text-slate-800 text-sm">{userObj.phoneNumber}</span>
                    </div>
                  )}
                </div>
                <div className="d-flex flex-wrap gap-2 align-items-center">
                  <Badge className="badge-pill px-3 py-2 text-uppercase font-weight-700 shadow-sm" style={{ backgroundColor: "#EEF2FF", color: "#4F46E5", border: "1.5px solid #C7D2FE", fontSize: "11px" }}>
                    <i className="fa fa-balance-scale mr-1" />
                    {t("categories.moderation.disputeFlag", "Dispute Flag")}
                  </Badge>
                  {flagPriority && (
                    <Badge className="badge-pill px-3 py-2 text-uppercase font-weight-700 shadow-sm" style={{ backgroundColor: getSeverityBadgeColor(flagPriority).bg, color: getSeverityBadgeColor(flagPriority).text, fontSize: "11px" }}>
                      {t("categories.moderation.severity." + flagPriority, flagPriority).toUpperCase()} {t("table.headers.priority", "Priority")}
                    </Badge>
                  )}
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* No dispute data fallback */}
        {!dispute && (
          <Row><Col xs="12">
            <Alert color="warning" className="mt-3">
              <i className="fa fa-exclamation-triangle mr-2" />
              {t("categories.moderation.noLinkedDispute", "No linked dispute found for this flag. The dispute may have been deleted or is not yet associated.")}
            </Alert>
          </Col></Row>
        )}

        {dispute && (
          <Row>
            {/* ── Left Column (lg=8) ── */}
            <Col lg="8" xs="12">

              {/* Dispute Information Card */}
              <Card className="shadow-sm border-0 rounded-lg mb-4">
                <CardHeader className="bg-white border-bottom py-3 d-flex justify-content-between align-items-center">
                  <h4 className="mb-0 font-weight-bold heading-md text-slate-800">
                    <i className="fa fa-balance-scale mr-2 text-primary" />
                    {t("categories.moderation.disputeInfo", "Dispute Information")}
                  </h4>
                  {dispute.ticketNumber && (
                    <Badge className="badge-pill px-3 py-2 font-weight-700 shadow-sm" style={{ backgroundColor: "#EEF2FF", color: "#4F46E5", fontSize: "12px" }}>
                      {dispute.ticketNumber}
                    </Badge>
                  )}
                </CardHeader>
                <CardBody className="p-4">
                  {/* Title + Description */}
                  <div className="mb-4 p-3 rounded" style={{ backgroundColor: "#F8FAFC", border: "1px dashed #E2E8F0" }}>
                    <span className="text-muted text-xs d-block text-uppercase font-weight-600 tracking-wider mb-1">
                      <i className="fa fa-comment-dots mr-1" />
                      {t("categories.moderation.disputeTitle", "Dispute Title")}
                    </span>
                    <p className="mb-2 text-sm text-slate-800 font-weight-700" style={{ lineHeight: 1.5 }}>{dispute.title || "—"}</p>
                    <span className="text-muted text-xs d-block text-uppercase font-weight-600 tracking-wider mb-1 mt-3">
                      <i className="fa fa-align-left mr-1" />
                      {t("categories.moderation.disputeDescription", "Description")}
                    </span>
                    <p className="mb-0 text-sm text-slate-700" style={{ lineHeight: 1.7 }}>{dispute.description || "—"}</p>
                  </div>

                  {/* 3-column Meta */}
                  <Row>
                    <Col md="4" xs="12" className="mb-3 mb-md-0">
                      <span className="text-muted text-xs d-block text-uppercase font-weight-600 tracking-wider mb-1">
                        <i className="fa fa-tag mr-1" />
                        {t("categories.moderation.category", "Category")}
                      </span>
                      <span className="font-weight-bold text-slate-800 text-sm">
                        {disputeCategoryLabel[dispute.category] || dispute.category?.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()) || "—"}
                      </span>
                    </Col>
                    <Col md="4" xs="12" className="mb-3 mb-md-0">
                      <span className="text-muted text-xs d-block text-uppercase font-weight-600 tracking-wider mb-1">
                        <i className="fa fa-user-tag mr-1" />
                        {t("categories.moderation.submittedBy", "Submitted By")}
                      </span>
                      <Badge className="text-uppercase" style={{ backgroundColor: dispute.role === "Sender" ? "#EFF6FF" : "#F0FDF4", color: dispute.role === "Sender" ? "#2563EB" : "#16A34A", fontSize: "11px", fontWeight: 700 }}>
                        {dispute.role || "—"}
                      </Badge>
                    </Col>
                    <Col md="4" xs="12">
                      <span className="text-muted text-xs d-block text-uppercase font-weight-600 tracking-wider mb-1">
                        <i className="fa fa-calendar-alt mr-1" />
                        {t("categories.moderation.submittedOn", "Submitted On")}
                      </span>
                      <span className="font-weight-bold text-slate-800 text-sm">
                        {dispute.createdAt ? helpers.getFormattedDate(dispute.createdAt, t) : "—"}
                      </span>
                    </Col>
                  </Row>

                  {/* Resolution Notes (if resolved) */}
                  {dispute.resolutionNotes && (
                    <div className="mt-4 p-3 rounded" style={{ backgroundColor: "#F0FDF4", border: "1px solid #BBF7D0" }}>
                      <span className="text-muted text-xs d-block text-uppercase font-weight-600 tracking-wider mb-2">
                        <i className="fa fa-check-circle mr-1 text-success" />
                        {t("categories.moderation.resolutionNotes", "Resolution Notes")}
                      </span>
                      <p className="mb-0 text-sm text-slate-700" style={{ lineHeight: 1.7 }}>{dispute.resolutionNotes}</p>
                      {dispute.resolvedAt && (
                        <small className="text-muted d-block mt-2">
                          {t("categories.moderation.resolvedAt", "Resolved on")} {helpers.getFormattedDate(dispute.resolvedAt, t)}
                        </small>
                      )}
                    </div>
                  )}
                </CardBody>
              </Card>

              {/* Linked Order Card */}
              {order ? (
                <Card className="shadow-sm border-0 rounded-lg mb-4">
                  <CardHeader className="bg-white border-bottom py-3 d-flex justify-content-between align-items-center">
                    <h4 className="mb-0 font-weight-bold heading-md text-slate-800">
                      <i className="fa fa-shopping-bag mr-2 text-primary" />
                      {t("categories.moderation.linkedOrder", "Linked Order")}
                    </h4>
                    <div className="d-flex align-items-center" style={{ gap: "8px" }}>
                      {order.orderid && (
                        <Badge className="badge-pill font-weight-700" style={{ backgroundColor: "#EFF6FF", color: "#2563EB", fontSize: "12px" }}>
                          {order.orderid}
                        </Badge>
                      )}
                      <Button
                        size="sm"
                        color="primary"
                        outline
                        className="font-weight-600"
                        style={{ borderRadius: "6px", fontSize: "12px" }}
                        onClick={() => navigate(`/admin/all-orders/orderDetails/${order._id}`)}
                      >
                        <i className="fa fa-external-link-alt mr-1" />
                        {t("categories.moderation.viewOrder", "View Order")}
                      </Button>
                    </div>
                  </CardHeader>
                  <CardBody className="p-4">
                    <Row>
                      {/* Order Status */}
                      <Col md="4" xs="12" className="mb-3 mb-md-0">
                        <span className="text-muted text-xs d-block text-uppercase font-weight-600 tracking-wider mb-1">
                          <i className="fa fa-info-circle mr-1" />
                          {t("table.headers.status", "Order Status")}
                        </span>
                        <Badge
                          className="badge-pill text-uppercase font-weight-700"
                          style={{
                            backgroundColor: (orderStatusColor[order.status] || "#8898aa") + "20",
                            color: orderStatusColor[order.status] || "#8898aa",
                            border: `1.5px solid ${(orderStatusColor[order.status] || "#8898aa")}50`,
                            fontSize: "11px"
                          }}
                        >
                          {order.status || "—"}
                        </Badge>
                      </Col>

                      {/* Payment — Sender */}
                      <Col md="4" xs="12" className="mb-3 mb-md-0">
                        <span className="text-muted text-xs d-block text-uppercase font-weight-600 tracking-wider mb-1">
                          <i className="fa fa-credit-card mr-1" />
                          {t("categories.moderation.senderPayment", "Sender Payment")}
                        </span>
                        <span className="font-weight-bold text-slate-800 text-sm text-capitalize">
                          {order.paymentDetails?.sender?.status?.replace(/_/g, " ") || "—"}
                        </span>
                      </Col>

                      {/* Payment — Traveler */}
                      <Col md="4" xs="12">
                        <span className="text-muted text-xs d-block text-uppercase font-weight-600 tracking-wider mb-1">
                          <i className="fa fa-credit-card mr-1" />
                          {t("categories.moderation.travelerPayment", "Traveler Payment")}
                        </span>
                        <span className="font-weight-bold text-slate-800 text-sm text-capitalize">
                          {order.paymentDetails?.traveler?.status?.replace(/_/g, " ") || "—"}
                        </span>
                      </Col>
                    </Row>

                    {/* Sender & Traveler profiles */}
                    <Row className="mt-3 pt-3" style={{ borderTop: "1px solid #EDF2F7" }}>
                      <Col md="6" xs="12" className="mb-3 mb-md-0">
                        <span className="text-muted text-xs d-block text-uppercase font-weight-600 tracking-wider mb-2">
                          <i className="fa fa-paper-plane mr-1" />
                          {t("table.headers.sender", "Sender")}
                        </span>
                        <UserMiniCard userObj={order.senderId} roleLabel={t("table.headers.sender", "Sender")} navigate={navigate} />
                      </Col>
                      <Col md="6" xs="12">
                        <span className="text-muted text-xs d-block text-uppercase font-weight-600 tracking-wider mb-2">
                          <i className="fa fa-suitcase mr-1" />
                          {t("table.headers.traveler", "Traveler")}
                        </span>
                        <UserMiniCard userObj={order.travelerId} roleLabel={t("table.headers.traveler", "Traveler")} navigate={navigate} />
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              ) : (
                <Alert color="info" className="mb-4">
                  <i className="fa fa-info-circle mr-2" />
                  Order details could not be loaded for this dispute.
                </Alert>
              )}

              {/* Evidence / Media Card */}
              {dispute.mediaUploads && dispute.mediaUploads.length > 0 && (
                <Card className="shadow-sm border-0 rounded-lg mb-4">
                  <CardHeader className="bg-white border-bottom py-3">
                    <h4 className="mb-0 font-weight-bold heading-md text-slate-800">
                      <i className="fa fa-paperclip mr-2 text-primary" />
                      {t("categories.moderation.evidenceTitle", "Evidence & Attachments")}
                      <Badge className="ml-2" style={{ backgroundColor: "#EFF6FF", color: "#2563EB", fontSize: "11px" }}>
                        {dispute.mediaUploads.length}
                      </Badge>
                    </h4>
                  </CardHeader>
                  <CardBody className="p-4">
                    <div className="d-flex flex-wrap" style={{ gap: "12px" }}>
                      {dispute.mediaUploads.map((url, idx) => {
                        const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(url);
                        const fullUrl = url.startsWith("http")
                          ? url
                          : !url.includes("/")
                          ? `${constants.s3Bucket}/disputes/${url}`
                          : getEvidenceUrl
                          ? getEvidenceUrl(url)
                          : `${constants.s3Bucket}/${url}`;
                        return (
                          <div key={idx} style={{ position: "relative" }}>
                            {isImage ? (
                              <div
                                onClick={() => {
                                  setSelectedEvidenceUrl(fullUrl);
                                  setEvidenceOpen(true);
                                }}
                                style={{ cursor: "pointer" }}
                              >
                                <img
                                  src={fullUrl}
                                  alt={`${t("categories.moderation.appeals.evidence", "Evidence")} ${idx + 1}`}
                                  style={{ width: "100px", height: "80px", objectFit: "cover", borderRadius: "8px", border: "1px solid #E2E8F0" }}
                                />
                              </div>
                            ) : (
                              <a
                                href={fullUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="d-flex flex-column align-items-center justify-content-center text-center text-muted"
                                style={{ width: "100px", height: "80px", borderRadius: "8px", border: "1px dashed #CBD5E0", backgroundColor: "#F8FAFC", fontSize: "11px", textDecoration: "none" }}
                              >
                                <i className="fa fa-file-alt" style={{ fontSize: "24px", marginBottom: "4px" }} />
                                {t("categories.moderation.file", "File")} {idx + 1}
                              </a>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </CardBody>
                </Card>
              )}

              {/* All Disputes for This Flag */}
              {flag.linkedDisputeIds && flag.linkedDisputeIds.length > 1 && (
                <Card className="shadow-sm border-0 rounded-lg mb-4">
                  <CardHeader className="bg-white border-bottom py-3 d-flex justify-content-between align-items-center">
                    <h4 className="mb-0 font-weight-bold heading-md text-slate-800">
                      <i className="fa fa-list-alt mr-2 text-primary" />
                      {t("categories.moderation.allLinkedDisputes", "All Linked Disputes")}
                    </h4>
                    <Badge className="badge-pill font-weight-700" style={{ backgroundColor: "#EEF2FF", color: "#4F46E5", fontSize: "11px" }}>
                      Total: {flag.linkedDisputeIds.length}
                    </Badge>
                  </CardHeader>
                  <CardBody className="p-0">
                    <div className="table-responsive">
                      <table className="table align-items-center table-flush mb-0">
                        <thead className="thead-light">
                          <tr>
                            <th className="font-weight-700 text-slate-600 text-xs">{t("table.headers.ticket", "Ticket")}</th>
                            <th className="font-weight-700 text-slate-600 text-xs">{t("table.headers.title", "Title")}</th>
                            <th className="font-weight-700 text-slate-600 text-xs">{t("table.headers.category", "Category")}</th>
                            <th className="font-weight-700 text-slate-600 text-xs">{t("table.headers.date", "Date")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {flag.linkedDisputeIds.map((d, idx) => (
                            <tr key={d._id} style={{ backgroundColor: idx === 0 ? "#FAFBFF" : "transparent" }}>
                              <td>
                                <span className="font-weight-bold text-sm" style={{ color: "#4F46E5" }}>{d.ticketNumber || "—"}</span>
                                {idx === 0 && <Badge className="ml-2 text-uppercase" style={{ backgroundColor: "#EEF2FF", color: "#4F46E5", fontSize: "9px" }}>{t("categories.moderation.latest", "Latest")}</Badge>}
                              </td>
                              <td className="text-sm text-slate-800 font-weight-500" style={{ maxWidth: "180px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.title}</td>
                              <td><span className="text-muted text-xs">{disputeCategoryLabel[d.category] || d.category?.replace(/_/g, " ")}</span></td>
                              <td><small className="text-slate-500">{helpers.getFormattedDate(d.createdAt, t)}</small></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardBody>
                </Card>
              )}
            </Col>

            {/* ── Right Column (lg=4) ── */}
            <Col lg="4" xs="12">
              {/* Dispute Status Card */}
              <Card className="shadow-sm border-0 rounded-lg mb-4">
                <CardHeader className="bg-white border-bottom py-3">
                  <h4 className="mb-0 font-weight-bold heading-md text-slate-800">
                    <i className="fa fa-info-circle mr-2 text-primary" />
                    {t("categories.moderation.disputeStatus", "Dispute Status")}
                  </h4>
                </CardHeader>
                <CardBody className="p-4">
                  {/* Key info */}
                  {[
                    { icon: "fa-hashtag", label: t("categories.moderation.ticketNumber", "Ticket Number"), value: dispute.ticketNumber || "—" },
                    { icon: "fa-calendar-alt", label: t("categories.moderation.submitted", "Submitted"), value: dispute.createdAt ? helpers.getFormattedDate(dispute.createdAt, t) : "—" },
                    { icon: "fa-flag", label: t("categories.moderation.flagCreated", "Flag Created"), value: flag.createdAt ? helpers.getFormattedDate(flag.createdAt, t) : "—" },
                    { icon: "fa-star", label: t("table.headers.priority", "Priority"), value: flagPriority ? t("categories.moderation.severity." + flagPriority, flagPriority).toUpperCase() : "—" },
                    { icon: "fa-user-tag", label: t("categories.moderation.raisedBy", "Raised By"), value: dispute.role ? t("table.headers." + dispute.role.toLowerCase(), dispute.role) : "—" },
                  ].map(({ icon, label, value }) => (
                    <div key={label} className="mb-3">
                      <span className="text-muted text-xs d-block text-uppercase font-weight-600 tracking-wider mb-1">
                        <i className={`fa ${icon} mr-1`} />{label}
                      </span>
                      <span className="font-weight-bold text-slate-800 text-sm">{value}</span>
                    </div>
                  ))}
                </CardBody>
              </Card>

              {/* Resolution Card (if resolved) */}
              {dispute.status === "resolved" && (
                <Card className="shadow-sm border-0 rounded-lg mb-4">
                  <CardHeader className="bg-white border-bottom py-3">
                    <h4 className="mb-0 font-weight-bold heading-md text-slate-800">
                      <i className="fa fa-check-circle mr-2 text-success" />
                      {t("categories.moderation.resolution", "Resolution")}
                    </h4>
                  </CardHeader>
                  <CardBody className="p-4">
                    <div className="mb-3">
                      <span className="text-muted text-xs d-block text-uppercase font-weight-600 tracking-wider mb-1">{t("categories.moderation.resolutionNotes", "Resolution Notes")}</span>
                      <p className="text-sm text-slate-800 mb-0" style={{ lineHeight: 1.6 }}>{dispute.resolutionNotes || "—"}</p>
                    </div>
                    {dispute.resolvedAt && (
                      <div>
                        <span className="text-muted text-xs d-block text-uppercase font-weight-600 tracking-wider mb-1">{t("categories.moderation.resolvedAt", "Resolved At")}</span>
                        <span className="font-weight-bold text-slate-800 text-sm">{helpers.getFormattedDate(dispute.resolvedAt, t)}</span>
                      </div>
                    )}
                    {dispute.assignedTo && (
                      <div className="mt-3">
                        <span className="text-muted text-xs d-block text-uppercase font-weight-600 tracking-wider mb-1">{t("table.headers.assignedModerator", "Assigned Moderator")}</span>
                        <span className="font-weight-bold text-slate-800 text-sm">
                          {typeof dispute.assignedTo === "object"
                            ? `${dispute.assignedTo.firstName || ""} ${dispute.assignedTo.lastName || ""}`.trim()
                            : "—"}
                        </span>
                      </div>
                    )}
                  </CardBody>
                </Card>
              )}
            </Col>
          </Row>
        )}
      </Container>

      {evidenceOpen && (
        <EvidenceModal isOpen={evidenceOpen} toggle={() => setEvidenceOpen(false)} fileUrl={selectedEvidenceUrl} />
      )}
    </>
  );
}

// ─── BEHAVIORAL DETAIL VIEW ───────────────────────────────────────────────────

function BehavioralDetailView({ flag, timeline, priorFlags, t, language, navigate, evidenceOpen, setEvidenceOpen, selectedEvidenceUrl, setSelectedEvidenceUrl, getEvidenceUrl, fetchFlagDetails, formatTimelineDate }) {
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const userObj = flag.userId || {};
  const userName = `${userObj.firstName || ""} ${userObj.lastName || ""}`.trim() || t("categories.moderation.unknown", "Unknown User");
  const userInitials = userName.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase() || "US";

  const totalRows = priorFlags.length;
  const paginatedPriorFlags = priorFlags.slice(
    currentPage * rowsPerPage,
    (currentPage + 1) * rowsPerPage
  );
  const evidenceUrl = getEvidenceUrl(flag.evidenceUrl);
  const statusColor = getStatusBadgeColor(getActiveDropdownItem(flag));
  const severityColors = getSeverityBadgeColor(flag.severity);
  const sortedTimeline = [...timeline].sort((a, b) => new Date(b.createdAt || b.timestamp) - new Date(a.createdAt || a.timestamp));
  const [selectedAction, setSelectedAction] = useState("");
  const [noteText, setNoteText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { notify } = useNotification();

  const handleActionUpdate = async () => {
    try {
      setSubmitting(true);
      let apiAction = selectedAction;
      if (apiAction === "dismissed" || apiAction === "closed") apiAction = "dismiss";
      const res = await userServices.saveDecision(flag._id, apiAction, t("categories.moderation.defaultActionNote", "Status updated by admin."));
      if (res && res.success) {
        notify("success", t("page.flagged.toast.statusUpdated", "Status updated successfully"));
        setSelectedAction("");
        fetchFlagDetails();
      } else {
        notify("error", res.error || t("page.flagged.toast.statusUpdateFailed", "Failed to save status change"));
      }
    } catch (err) {
      notify("error", t("page.flagged.toast.genericError", "An error occurred. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveNote = async () => {
    if (noteText.trim().length < 20) {
      notify("error", t("categories.moderation.notesRequiredError", "Note must be at least 20 characters."));
      return;
    }
    try {
      setSubmitting(true);
      const res = await userServices.saveDecision(flag._id, "note", noteText.trim());
      if (res && res.success) {
        notify("success", t("page.flagged.toast.statusUpdated", "Note saved successfully"));
        setNoteText("");
        fetchFlagDetails();
      } else {
        notify("error", res.error || t("page.flagged.toast.statusUpdateFailed", "Failed to save note"));
      }
    } catch (err) {
      notify("error", t("page.flagged.toast.genericError", "An error occurred. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <SimpleHeader
        name={t("categories.moderation.behavioralDetails", "Behavioral Investigation")}
        parentName={t("sideNav.behavioralQueue", "Behavioral & Disputes")}
      />
      <Container className="mt--6" fluid>

        {/* ── Profile Header Card ── */}
        <Row className="mb--2">
          <Col xs="12">
            <Card className="shadow-sm border-0 rounded-lg">
              <CardBody className="py-4 px-4 d-flex flex-column flex-md-row justify-content-between align-items-md-center">
                <div className="d-flex align-items-center mb-3 mb-md-0">
                  <div
                    className="d-flex align-items-center justify-content-center rounded-circle text-primary font-weight-bold mr-3"
                    style={{ width: "60px", height: "60px", backgroundColor: "#EFF6FF", fontSize: "20px", border: "1px solid #DBEAFE", flexShrink: 0 }}
                  >
                    {userInitials}
                  </div>
                  <div>
                    <h2 className="mb-0 text-slate-800 font-weight-bold" style={{ fontSize: "20px" }}>{userName}</h2>
                    <span className="text-muted text-sm">{userObj.email || ""}</span>
                  </div>
                  {userObj.phoneNumber && (
                    <div className="border-left pl-4 ml-4 d-none d-lg-block">
                      <span className="text-muted text-xs d-block text-uppercase font-weight-600 tracking-wider">{t("table.headers.phone", "Phone")}</span>
                      <span className="font-weight-bold text-slate-800 text-sm">{userObj.phoneNumber}</span>
                    </div>
                  )}
                </div>
                <div className="d-flex flex-wrap gap-2 align-items-center">
                  <Badge className="badge-pill px-3 py-2 text-uppercase font-weight-700 shadow-sm" style={{ backgroundColor: statusColor + "18", color: statusColor, border: `1.5px solid ${statusColor}50`, fontSize: "11px" }}>
                    {t("categories.moderation.actionTaken", "Action Taken:")} {getStatusDisplay(flag, t)}
                  </Badge>
                  <Badge className="badge-pill px-3 py-2 text-uppercase font-weight-700 shadow-sm" style={{ backgroundColor: severityColors.bg, color: severityColors.text, fontSize: "11px" }}>
                    {t("categories.moderation.severity." + flag.severity, flag.severity).toUpperCase()} {t("table.headers.priority", "Priority")}
                  </Badge>
                  <Badge className="badge-pill px-3 py-2 text-uppercase font-weight-700 ml-md-2 shadow-sm status-info">
                    {t("categories.moderation.flag", "Flag:")} {t("page.flagged.status." + flag.status, flag.status)}
                  </Badge>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* ── Main 2-column Row ── */}
        <Row>
          {/* Left Column */}
          <Col lg="8" xs="12">

            {/* Violation Context Card */}
            <Card className="shadow-sm border-0 rounded-lg mb-4">
              <CardHeader className="bg-white border-bottom py-3 d-flex justify-content-between align-items-center">
                <h4 className="mb-0 font-weight-bold heading-md text-slate-800">
                  <i className="fa fa-exclamation-triangle mr-2 text-warning" />
                  {t("categories.moderation.violationContext", "Violation Context")}
                </h4>

              </CardHeader>
              <CardBody className="p-4">
                {/* Flagged message block */}
                <div className="mb-4 p-3 rounded" style={{ backgroundColor: "#FFF9F5", border: "1px dashed #FED7AA" }}>
                  <span className="text-muted text-xs d-block text-uppercase font-weight-600 tracking-wider mb-2">
                    <i className="fa fa-comment-alt mr-1 text-warning" />
                    {t("categories.moderation.flaggedMessage", "Flagged Message / Context")}
                  </span>
                  <p className="mb-0 font-italic text-slate-800 text-sm" style={{ lineHeight: 1.7 }}>
                    &ldquo;{formatContext(flag.context, t) || t("categories.moderation.noContext", "No context registered.")}&rdquo;
                  </p>
                </div>

                {/* 3-column Metadata Row */}
                <Row>
                  <Col md="4" xs="12" className="mb-3 mb-md-0">
                    <span className="text-muted text-xs d-block text-uppercase font-weight-600 tracking-wider mb-1">
                      <i className="fa fa-calendar-alt mr-1" />
                      {t("categories.moderation.firstReported", "First Reported")}
                    </span>
                    <span className="font-weight-bold text-slate-800 text-sm">{flag.createdAt ? helpers.getFormattedDate(flag.createdAt, t) : "—"}</span>
                  </Col>
                  <Col md="4" xs="12" className="mb-3 mb-md-0">
                    <span className="text-muted text-xs d-block text-uppercase font-weight-600 tracking-wider mb-1">
                      <i className="fa fa-clock mr-1" />
                      {t("categories.moderation.lastUpdated", "Last Updated")}
                    </span>
                    <span className="font-weight-bold text-slate-800 text-sm">{flag.updatedAt ? helpers.getFormattedDate(flag.updatedAt, t) : "—"}</span>
                  </Col>
                  <Col md="4" xs="12">
                    <span className="text-muted text-xs d-block text-uppercase font-weight-600 tracking-wider mb-1">
                      <i className="fa fa-shield-alt mr-1" />
                      {t("table.headers.source", "Source")}
                    </span>
                    <span className="font-weight-bold text-slate-800 text-sm">
                      {t("categories.moderation.automatic", "Automated System")}
                    </span>
                  </Col>
                </Row>

                {flag.evidenceUrl && (
                  <div className="d-flex mt-4 pt-3" style={{ gap: "8px", borderTop: "1px solid #EDF2F7" }}>
                    <Button size="sm" color="primary" outline className="font-weight-600" style={{ borderRadius: "6px", fontSize: "12px" }} onClick={() => { setSelectedEvidenceUrl(evidenceUrl); setEvidenceOpen(true); }}>
                      <i className="fa fa-eye mr-1" /> {t("common.preview", "Preview")}
                    </Button>
                    <Button size="sm" color="secondary" outline className="font-weight-600" style={{ borderRadius: "6px", fontSize: "12px" }} href={evidenceUrl} target="_blank" download>
                      <i className="fa fa-download mr-1" /> {t("common.download", "Download")}
                    </Button>
                    <Button size="sm" color="secondary" outline className="font-weight-600" style={{ borderRadius: "6px", fontSize: "12px" }} href={evidenceUrl} target="_blank" rel="noopener noreferrer">
                      <i className="fa fa-external-link-alt mr-1" /> {t("common.openOriginal", "Open Original")}
                    </Button>
                  </div>
                )}
              </CardBody>
            </Card>

            {flag?.flagType === "behavioral" && (
              <Card className="shadow-sm border-0 rounded-lg mb-4">
                <CardHeader className="bg-white border-bottom py-3">
                  <h4 className="mb-0 font-weight-bold heading-md text-slate-800">
                    <i className="fa fa-users mr-2 text-primary" />
                    {t("categories.moderation.userHistoryTitle", "User Compliance History")}
                  </h4>
                </CardHeader>
                <CardBody className="p-0">
                  {priorFlags.length === 0 ? (
                    <p className="text-center py-4 text-muted text-sm">{t("categories.moderation.noPriorFlags", "No prior warnings or flags.")}</p>
                  ) : (
                    <div className="table-responsive">
                      <table className="table align-items-center table-flush mb-0">
                        <thead className="thead-light">
                          <tr>
                            <th className="font-weight-700 text-slate-600 text-xs">{t("table.headers.type", "TYPE")}</th>
                            <th className="font-weight-700 text-slate-600 text-xs">{t("table.headers.severity", "SEVERITY")}</th>
                            <th className="font-weight-700 text-slate-600 text-xs">{t("table.headers.status", "STATUS")}</th>
                            <th className="font-weight-700 text-slate-600 text-xs">{t("table.headers.date", "DATE")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {paginatedPriorFlags.map((uf) => {
                            const sevColors = getSeverityBadgeColor(uf.severity);
                            const resolvedStatus = uf.status === "decision_made" ? (uf.actionTaken || uf.status) : uf.status;
                            const stColor = getStatusBadgeColor(resolvedStatus);
                            return (
                              <tr key={uf._id}>
                                <td>
                                  <span className="font-weight-bold text-slate-800 text-sm d-block">{uf.flagType?.charAt(0).toUpperCase() + uf.flagType?.slice(1)}</span>
                                  {uf.contentCategory && <small className="text-muted text-xs">{t(`page.flagged.categories.${uf.contentCategory}`, uf.contentCategory)}</small>}
                                </td>
                                <td>
                                  <Badge style={{ backgroundColor: sevColors.bg, color: sevColors.text, fontSize: "10px", fontWeight: 700, letterSpacing: "0.04em" }} className="text-uppercase">
                                    {getSeverityLabel(uf.severity)}
                                  </Badge>
                                </td>
                                <td>
                                  <Badge style={{ backgroundColor: stColor + "20", color: stColor, fontSize: "10px", fontWeight: 700, border: `1px solid ${stColor}40`, letterSpacing: "0.04em" }} className="text-uppercase">
                                    {getStatusLabel(resolvedStatus, t)}
                                  </Badge>
                                </td>
                                <td><small className="text-slate-500 font-weight-500">{helpers.getFormattedDate(uf.createdAt, t)}</small></td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                  {priorFlags.length > 0 && (
                    <div className="py-2 border-top">
                      <Pagination
                        currentPage={currentPage}
                        rowsPerPage={rowsPerPage}
                        totalRows={totalRows}
                        onPageChange={(page) => setCurrentPage(page)}
                        onRowsPerPageChange={(size) => {
                          setRowsPerPage(size);
                          setCurrentPage(0);
                        }}
                        rowsPerPageOptions={[5, 10, 20]}
                        t={t}
                      />
                    </div>
                  )}
                </CardBody>
              </Card>
            )}

            {/* Timeline & Audit Log */}
            <Card className="shadow-sm border-0 rounded-lg mb-4">
              <CardHeader className="bg-white border-bottom py-3">
                <h4 className="mb-0 font-weight-bold heading-md text-slate-800">
                  <i className="fa fa-history mr-2 text-primary" />
                  {t("categories.moderation.timelineTitle", "Timeline & Audit Log")}
                </h4>
              </CardHeader>
              <CardBody className="p-0">
                <div className="table-responsive">
                  <table className="table align-items-center table-flush mb-0" style={{ tableLayout: "fixed" }}>
                    <thead className="thead-light">
                      <tr>
                        <th className="font-weight-700 text-slate-600 text-xs" style={{ width: "22%" }}>
                          {t("table.headers.actor", "Actor")}
                        </th>

                        <th className="font-weight-700 text-slate-600 text-xs" style={{ width: "26%" }}>
                          {t("table.headers.transition", "Transition")}
                        </th>

                        <th className="font-weight-700 text-slate-600 text-xs" style={{ width: "32%" }}>
                          {t("table.headers.adminNote", "Admin Note")}
                        </th>

                        <th className="font-weight-700 text-slate-600 text-xs" style={{ width: "20%" }}>
                          {t("table.headers.dateTime", "Date & Time")}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedTimeline.length === 0 ? (
                        <tr><td colSpan="4" className="text-center py-4 text-muted text-sm" style={{ fontStyle: "italic" }}>{t("categories.moderation.noTimeline", "No moderation history available.")}</td></tr>
                      ) : (
                        sortedTimeline.map((log, index) => {
                          const actorName = log.actorId ? `${log.actorId.firstName || ""} ${log.actorId.lastName || ""}`.trim() || log.actorId.email : t("categories.moderation.system", "System");
                          const actorRole = log.actorId?.role?.name || "System";
                          const prevStatus = log.previousState?.status;
                          const newStatus = log.newState?.status;
                          const prevAction = log.previousState?.actionTaken;
                          const newAction = log.newState?.actionTaken;
                          const displayPrev = prevAction && prevAction !== "none" ? prevAction : (prevStatus || "");
                          const displayNew = newAction && newAction !== "none" ? newAction : (newStatus || "");
                          const showTransition = displayPrev && displayNew && displayPrev !== displayNew;
                          return (
                            <tr key={log._id || index}>
                              <td>
                                <span className="font-weight-bold text-slate-800 text-sm d-block">{actorName}</span>
                                <small className="text-muted" style={{ fontSize: "10px" }}>{actorRole}</small>
                              </td>
                              <td>
                                {showTransition ? (
                                  <div className="d-inline-flex align-items-center" style={{ gap: "4px", flexWrap: "wrap" }}>
                                    <Badge style={{ backgroundColor: getStatusBadgeColor(displayPrev) + "18", color: getStatusBadgeColor(displayPrev), fontSize: "10px", border: `1px solid ${getStatusBadgeColor(displayPrev)}40`, fontWeight: 700 }} className="text-uppercase">
                                      {getStatusLabel(displayPrev, t)}
                                    </Badge>
                                    <i className="fa fa-arrow-right" style={{ fontSize: "8px", color: "#A0AEC0" }} />
                                    <Badge style={{ backgroundColor: getStatusBadgeColor(displayNew) + "18", color: getStatusBadgeColor(displayNew), fontSize: "10px", border: `1px solid ${getStatusBadgeColor(displayNew)}40`, fontWeight: 700 }} className="text-uppercase">
                                      {getStatusLabel(displayNew, t)}
                                    </Badge>
                                  </div>
                                ) : (
                                  <span className="text-muted text-xs">—</span>
                                )}
                              </td>
                              <td className="text-sm text-slate-700" style={{ whiteSpace: "normal", maxWidth: "200px" }}>
                                {helpers.formatNotesDates(log.notes) || "—"}
                              </td>
                              <td><small className="text-slate-500 font-weight-500">{formatTimelineDate(log.createdAt || log.timestamp)}</small></td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </CardBody>
            </Card>
          </Col>

          {/* Right Column: Admin Actions */}
          <Col lg="4" xs="12">
            <Card className="shadow-sm border-0 rounded-lg">
              <CardHeader className="bg-white border-bottom py-3">
                <h4 className="mb-0 font-weight-bold heading-md text-slate-800">
                  <i className="fa fa-gavel mr-2 text-primary" />
                  {t("categories.moderation.verification.adminActions", "Admin Actions")}
                </h4>
              </CardHeader>
              <CardBody className="p-4">
                <div className="mb-3">
                  <span className="text-muted text-xs d-block text-uppercase font-weight-600 tracking-wider mb-2">{t("categories.moderation.currentStatus", "Current Status")}</span>
                  <div className="d-flex align-items-center p-3 rounded" style={{ backgroundColor: statusColor + "10", border: `1.5px solid ${statusColor}35` }}>
                    <span style={{ display: "inline-block", width: "8px", height: "8px", borderRadius: "50%", backgroundColor: statusColor, marginRight: "8px", flexShrink: 0 }} />
                    <span className="text-sm font-weight-bold text-slate-800">{getStatusDisplay(flag, t)}</span>
                  </div>
                </div>
                <hr className="my-3" />
                <div className="mb-3">
                  <span className="text-muted text-xs d-block text-uppercase font-weight-600 tracking-wider mb-2">{t("categories.moderation.takeAction", "Take Action")}</span>
                  <div className="d-flex flex-wrap" style={{ gap: "6px" }}>
                    {["dismiss", "warn", "restrict", "suspend"].map(action => {
                      const isActive = selectedAction === action;
                      const actionColors = {
                        dismiss: { bg: "#2dce89", label: t("page.flagged.status.dismiss", "Dismiss") },
                        warn: { bg: "#fb6340", label: t("page.flagged.status.warn", "Warn") },
                        restrict: { bg: "#11cdef", label: t("page.flagged.status.restrict", "Restrict") },
                        suspend: { bg: "#f5365c", label: t("page.flagged.status.suspend", "Suspend") },
                      };
                      const ac = actionColors[action];
                      return (
                        <button
                          key={action}
                          type="button"
                          onClick={() => { setSelectedAction(isActive ? "" : action); }}
                          style={{
                            padding: "6px 14px",
                            fontSize: "11px",
                            fontWeight: 700,
                            borderRadius: "6px",
                            border: isActive ? `2px solid ${ac.bg}` : `1.5px solid ${ac.bg}40`,
                            backgroundColor: isActive ? `${ac.bg}18` : "transparent",
                            color: isActive ? ac.bg : "#64748B",
                            cursor: "pointer",
                            textTransform: "uppercase",
                            letterSpacing: "0.04em",
                            transition: "all 0.15s ease",
                          }}
                        >
                          {ac.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
                {selectedAction && (
                  <div className="d-flex" style={{ gap: "8px" }}>
                    <Button
                      color="secondary"
                      size="sm"
                      outline
                      className="font-weight-600"
                      style={{ flex: 1, borderRadius: "6px", fontSize: "12px" }}
                      onClick={() => { setSelectedAction(""); }}
                      disabled={submitting}
                    >
                      {t("common.cancel", "Cancel")}
                    </Button>
                    <Button
                      color="primary"
                      size="sm"
                      className="font-weight-600"
                      style={{ flex: 1, borderRadius: "6px", fontSize: "12px" }}
                      onClick={handleActionUpdate}
                      disabled={submitting}
                    >
                      {submitting ? <Spinner size="sm" /> : t("common.update", "Update")}
                    </Button>
                  </div>
                )}
                <hr className="my-3" />
                <div className="mb-3">
                  <span className="text-muted text-xs d-block text-uppercase font-weight-600 tracking-wider mb-2">{t("categories.moderation.addAdminNote", "Add Admin Note")}</span>
                  <FormGroup className="mb-2">
                    <Input
                      type="textarea"
                      rows="3"
                      value={noteText}
                      onChange={(e) => setNoteText(e.target.value)}
                      placeholder={t("categories.moderation.notesPlaceholder", "Add a note about this action...")}
                      invalid={noteText.length > 0 && noteText.length < 20}
                      className="border rounded-lg p-2 text-sm"
                      style={{ resize: "vertical" }}
                    />
                    <div className="text-right mt-1 text-xs text-muted">{noteText.length} / 20</div>
                  </FormGroup>
                  {noteText && (
                    <Button
                      color="primary"
                      size="sm"
                      className="font-weight-600"
                      style={{ borderRadius: "6px", fontSize: "12px" }}
                      onClick={handleSaveNote}
                      disabled={noteText.trim().length < 20 || submitting}
                    >
                      {submitting ? <Spinner size="sm" /> : t("common.submit", "Submit Note")}
                    </Button>
                  )}
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      {evidenceOpen && (
        <EvidenceModal isOpen={evidenceOpen} toggle={() => setEvidenceOpen(false)} fileUrl={selectedEvidenceUrl} />
      )}
    </>
  );
}

// ─── MAIN PAGE COMPONENT ──────────────────────────────────────────────────────

function BehavioralDetailsPage() {
  const { flagId } = useParams();
  const navigate = useNavigate();
  const { t, language } = useTranslation();

  const [flag, setFlag] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [priorFlags, setPriorFlags] = useState([]);
  const [evidenceOpen, setEvidenceOpen] = useState(false);
  const [selectedEvidenceUrl, setSelectedEvidenceUrl] = useState("");

  const getEvidenceUrl = useCallback((url) => {
    if (!url) return "";
    if (url.startsWith("http")) return url;
    const s3Root = constants.s3Bucket.replace(/\/assets$/, "");
    return url.startsWith("assets/") ? `${s3Root}/${url}` : `${constants.s3Bucket}/${url}`;
  }, []);

  const fetchFlagDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await userServices.getFlagDetails(flagId);
      if (!res || !res.success) {
        setError(res?.error || t("page.flagged.toast.fetchError", "Failed to fetch flag details."));
        return;
      }
      const flagData = res.data;
      setFlag(flagData);

      // Only fetch timeline + prior flags for behavioral flags
      if (flagData.flagType === "behavioral") {
        const timelineRes = await userServices.getTimeline(flagId);
        const timelineLogs = timelineRes?.success ? (timelineRes.data || timelineRes) : [];
        setTimeline(timelineLogs);

        if (flagData.userId?._id) {
          const priorRes = await userServices.getFlaggedByUserId(flagData.userId._id);
          const priorLogs = priorRes?.success ? (priorRes.data || []) : [];
          setPriorFlags(priorLogs);
        }
      }
    } catch (err) {
      console.error(err);
      setError(t("page.flagged.toast.genericError", "Failed to load details."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { 
    fetchFlagDetails(); 
    // eslint-disable-next-line
  }, [flagId]);

  const formatTimelineDate = (dateVal) => {
    if (!dateVal) return "";
    const d = new Date(dateVal);
    if (isNaN(d.getTime())) return "";
    return d.toLocaleDateString(language, { day: "2-digit", month: "short", year: "numeric" }) + " • " + d.toLocaleTimeString(language, { hour: "2-digit", minute: "2-digit" });
  };

  if (loading) {
    return (
      <div className="text-center py-5" style={{ minHeight: "300px", marginTop: "100px" }}>
        <Spinner color="primary" />
        <p className="mt-2 text-muted">{t("common.loading", "Loading details...")}</p>
      </div>
    );
  }

  if (error || !flag) {
    return (
      <Container className="mt-5">
        <Alert color="danger" className="p-4">
          <h4 className="alert-heading">{t("common.error", "Error Loading Details")}</h4>
          <p>{error || t("page.flagged.toast.notFound", "Flag not found.")}</p>
          <hr />
          <Button color="secondary" onClick={() => navigate("/admin/moderation/behavioral")}>{t("common.backToQueue", "Back to Behavioral Queue")}</Button>
        </Alert>
      </Container>
    );
  }

  // ── Route by flagType ────────────────────────────────────────────────────────
  if (flag.flagType === "dispute") {
    return <DisputeDetailView flag={flag} t={t} navigate={navigate} getEvidenceUrl={getEvidenceUrl} />;
  }

  // ── Behavioral (default) ─────────────────────────────────────────────────────
  return (
    <BehavioralDetailView
      flag={flag}
      timeline={timeline}
      priorFlags={priorFlags}
      t={t}
      language={language}
      navigate={navigate}
      evidenceOpen={evidenceOpen}
      setEvidenceOpen={setEvidenceOpen}
      selectedEvidenceUrl={selectedEvidenceUrl}
      setSelectedEvidenceUrl={setSelectedEvidenceUrl}
      getEvidenceUrl={getEvidenceUrl}
      fetchFlagDetails={fetchFlagDetails}
      formatTimelineDate={formatTimelineDate}
    />
  );
}

export default BehavioralDetailsPage;
